/**
 * RelayPolicyBlocksPage
 * Blocked-by-policy events list with override action.
 * Route: /settings/agent-email/policy-blocks (the "Held messages" tab of SettingsAgentEmailPage)
 * US-8
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fetchPolicyBlocks,
  overridePolicyBlock,
  type PolicyBlockEvent,
} from '../lib/relay.api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

// ---------------------------------------------------------------------------
// Override confirm modal
// ---------------------------------------------------------------------------

interface PolicyOverrideModalProps {
  eventId: string;
  gateSlug: string | undefined;
  onConfirm: (note: string) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
}

function PolicyOverrideModal({
  eventId,
  gateSlug,
  onConfirm,
  onClose,
  isSubmitting,
}: PolicyOverrideModalProps) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      await onConfirm(note.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply override');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="policy-override-modal-title"
        className="bg-card rounded-lg border border-border w-[90%] max-w-[500px] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="policy-override-modal-title"
          className="text-lg font-semibold text-foreground mb-2"
        >
          Override Policy Block
        </h2>

        <div
          className="p-3 mb-4 bg-warning/10 border border-warning/30 rounded-md text-warning text-sm"
          role="alert"
        >
          <strong>Warning:</strong> Overriding a policy block bypasses the configured gate. This
          action is logged and audited.
          {gateSlug && (
            <span className="block mt-1 font-mono text-xs">Gate: {gateSlug}</span>
          )}
          <span className="block mt-1 font-mono text-xs">Event: {eventId.slice(0, 12)}...</span>
        </div>

        <div className="mb-4">
          <label
            htmlFor="policy-override-note"
            className="block text-sm font-medium text-foreground mb-1"
          >
            Note (optional)
          </label>
          <textarea
            id="policy-override-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Reason for overriding this policy block..."
            className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground outline-none resize-y font-[inherit]"
            data-testid="policy-override-note-input"
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? 'policy-override-error' : undefined}
          />
        </div>

        {error && (
          <p id="policy-override-error" className="text-sm text-destructive mb-3" role="alert">
            {error}
          </p>
        )}

        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            data-testid="policy-override-confirm-button"
          >
            {isSubmitting ? 'Applying...' : 'Override'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const PAGE_SIZE = 20;

export function RelayPolicyBlocksPage() {
  const [events, setEvents] = useState<PolicyBlockEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overrideEventId, setOverrideEventId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPolicyBlocks({ page, limit: PAGE_SIZE });
      setEvents(data.events);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load policy blocks');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleOverrideConfirm = useCallback(
    async (note: string) => {
      if (!overrideEventId) return;
      setIsSubmitting(true);
      try {
        const result = await overridePolicyBlock(overrideEventId, { note: note || undefined });
        setEvents((prev) =>
          prev.map((e) => (e.id === overrideEventId ? result.event : e))
        );
        setOverrideEventId(null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [overrideEventId]
  );

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const overrideEvent = overrideEventId
    ? events.find((e) => e.id === overrideEventId)
    : null;

  return (
    <div data-testid="relay-policy-blocks-page">
      {/* Error state */}
      {error && (
        <div
          data-testid="policy-blocks-error"
          className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
          role="alert"
        >
          <span>{error}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={loadEvents}
            data-testid="policy-blocks-retry"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div data-testid="policy-blocks-loading" className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {events.length === 0 ? (
            <div
              data-testid="policy-blocks-empty"
              className="py-12 text-center text-muted-foreground"
            >
              <p className="text-sm">No blocked outbound events.</p>
              <p className="text-xs mt-1">When an agent's outbound message is blocked by a policy gate, it will appear here for your review and optional override.</p>
            </div>
          ) : (
            <div
              data-testid="policy-blocks-table"
              className="rounded-lg border border-border overflow-hidden"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm" aria-label="Policy block events table">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border">
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Case
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Gate
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Action
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Blocked At
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Override
                      </th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((evt, index) => {
                      const isOverridden = !!evt.payload?.overrideAppliedAt;
                      return (
                        <tr
                          key={evt.id}
                          data-testid={`policy-block-row-${evt.id}`}
                          className={`${
                            index < events.length - 1 ? 'border-b border-border' : ''
                          } ${isOverridden ? 'opacity-60' : ''}`}
                        >
                          <td className="px-4 py-3 font-mono text-xs text-foreground">
                            {evt.caseId.slice(0, 12)}...
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-foreground" data-testid={`policy-block-gate-slug-${evt.id}`}>
                            {evt.payload?.gateSlug ?? '—'}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground capitalize">
                            {evt.payload?.blockAction ?? evt.eventType}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">
                            {formatDate(evt.createdAt)}
                          </td>
                          <td className="px-4 py-3">
                            {isOverridden ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success border border-success/30">
                                Overridden
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {!isOverridden && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setOverrideEventId(evt.id)}
                                data-testid={`policy-override-button-${evt.id}`}
                              >
                                Override
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              data-testid="policy-blocks-pagination"
              className="mt-4 flex items-center justify-between"
            >
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({total} total)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  data-testid="policy-blocks-prev-page"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="policy-blocks-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Override modal */}
      {overrideEventId && overrideEvent && (
        <PolicyOverrideModal
          eventId={overrideEventId}
          gateSlug={overrideEvent.payload?.gateSlug}
          onConfirm={handleOverrideConfirm}
          onClose={() => setOverrideEventId(null)}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}
